import { Component, computed, effect, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, NG_VALIDATORS, ReactiveFormsModule, Validator, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, switchMap, filter, tap, catchError, of, Observable } from 'rxjs';
import { RegisterService } from '../../services/register.service';
import { CompanyDetails, CompanyPersonnel } from '../../models/registration.models';
import { AddCompanyDialogComponent, AddCompanyDialogResult } from '../add-company-dialog.component';

interface ContactOption {
  id: string;
  displayName: string;
  personnel?: CompanyPersonnel;
  isAddNew?: boolean;
}

export interface CustomerDetailsValue {
  registeringAs: 'individual' | 'company';
  personalDetails: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  companyDetails: {
    companyNumber: string;
    companyName: string;
  };
}

@Component({
  selector: 'app-customer-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatSelectModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerDetailsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomerDetailsComponent),
      multi: true
    }
  ],
  templateUrl: './customer-details.component.html',
  styleUrls: ['./customer-details.component.scss']
})
export class CustomerDetailsComponent implements ControlValueAccessor, Validator {
  private fb = inject(FormBuilder);
  private regService = inject(RegisterService);
  private dialog = inject(MatDialog);

  // Signals for reactive state management
  readonly registeringAs = signal<'individual' | 'company'>('individual');
  readonly fetchingCompanyDetails = signal<boolean>(false);
  readonly filteredOptions = signal<Observable<CompanyDetails[]>>(of([]));
  readonly companyPersonnel = signal<CompanyPersonnel[]>([]);
  readonly fetchingPersonnel = signal<boolean>(false);
  readonly selectedCompany = signal<CompanyDetails | null>(null);
  
  // Computed signals
  readonly isCompanySelected = computed(() => this.registeringAs() === 'company');
  readonly showCompanyEmailHint = computed(() => this.isCompanySelected());
  readonly contactOptions = computed(() => {
    const personnel = this.companyPersonnel();
    const options: ContactOption[] = personnel.map(person => ({
      id: `${person.firstName}_${person.lastName}_${person.email}`,
      displayName: `${person.firstName} ${person.lastName}`,
      personnel: person
    }));
    
    // Add "Add new contact" option
    options.push({
      id: 'add_new',
      displayName: 'Add new contact',
      isAddNew: true
    });
    
    return options;
  });

  // Custom validator to ensure company was selected from autocomplete
  private companySelectionValidator(group: AbstractControl): ValidationErrors | null {
    const companyName = group.get('companyName')?.value;
    const companyNumber = group.get('companyNumber')?.value;
    
    if (companyName && !companyNumber) {
      return { companyNotSelected: true };
    }
    
    return null;
  }

  // Form setup
  readonly customerForm: FormGroup;

  // ControlValueAccessor properties
  private onChange = (value: CustomerDetailsValue) => {};
  private onTouched = () => {};
  private onValidatorChange = () => {};
  private isDisabled = false;

  constructor() {
    this.customerForm = this.fb.group({
      registeringAs: ['individual', Validators.required],
      personalDetails: this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.required, Validators.pattern('^((\\+44\\s?7\\d{3}|\\(?07\\d{3}\\)?)\\s?\\d{3}\\s?\\d{3}$|(?:\\+44\\s?2\\d{2}|0(?:20|2\\d{2}))\\s?\\d{4}\\s?\\d{4}$|(?:\\+44\\s?1\\d{3}|01\\d{3})\\s?\\d{3}\\s?\\d{3,4}$)')]], 
      }),
      companyDetails: this.fb.group({
        companyNumber: ['', Validators.required],
        companyName: ['', Validators.required],
        contact: ['']
      }, { validators: this.companySelectionValidator.bind(this) })
    });

    // Initially disable company details
    this.customerForm.get('companyDetails')?.disable();

    // Effect to handle registeringAs changes
    effect(() => {
      const registeringAs = this.registeringAs();
      this.customerForm.get('registeringAs')?.setValue(registeringAs, { emitEvent: false });
      
      if (registeringAs === 'company') {
        this.customerForm.get('companyDetails')?.enable();
      } else {
        this.customerForm.get('companyDetails')?.disable();
      }
    });

    // Effect to setup company autocomplete when company is selected
    effect(() => {
      if (this.isCompanySelected()) {
        this.setupCompanyAutocomplete();
      }
    });

    // Watch for form changes and emit to parent
    this.customerForm.valueChanges.subscribe(value => {
      if (!this.isDisabled) {
        // Exclude contact field from the emitted value
        const sanitizedValue = {
          ...value,
          companyDetails: {
            companyNumber: value.companyDetails?.companyNumber,
            companyName: value.companyDetails?.companyName
          }
        };
        this.onChange(sanitizedValue);
        this.onTouched();
        this.onValidatorChange(); // Trigger validation update
      }
    });

    // Watch for form status changes to trigger validation
    this.customerForm.statusChanges.subscribe(() => {
      this.onValidatorChange();
    });

    // Watch for registeringAs changes from form
    this.customerForm.get('registeringAs')?.valueChanges.subscribe(value => {
      this.registeringAs.set(value);
    });
  }

  private setupCompanyAutocomplete(): void {
    const companyNameControl = this.customerForm.get('companyDetails.companyName');
    const companyNumberControl = this.customerForm.get('companyDetails.companyNumber');
    if (!companyNameControl || !companyNumberControl) return;

    // Watch for manual typing to clear companyNumber (validation mechanism)
    companyNameControl.valueChanges.pipe(
      tap(() => {
        // Clear companyNumber when user types manually (not from selection)
        // This will be set back when a valid option is selected
        if (companyNumberControl.value) {
          companyNumberControl.setValue('', { emitEvent: false });
        }
        // Clear selected company and personnel when typing
        this.selectedCompany.set(null);
        this.companyPersonnel.set([]);
        this.customerForm.get('companyDetails.contact')?.setValue('', { emitEvent: false });
      })
    ).subscribe();

    const options$ = companyNameControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((value: string) => {
        return !!value && typeof value === 'string' && value.trim().length >= 3;
      }),
      tap(() => this.fetchingCompanyDetails.set(true)),
      switchMap(value => {
        return this.regService.searchCompany(value).pipe(
          catchError((error) => {
            console.error('Error fetching company details:', error);
            return of([]);
          })
        );
      }),
      tap(() => this.fetchingCompanyDetails.set(false))
    );

    this.filteredOptions.set(options$);
  }

  onOptionSelected(value: CompanyDetails): void {
    this.selectedCompany.set(value);
    this.customerForm.get('companyDetails.companyName')?.setValue(value.name);
    this.customerForm.get('companyDetails.companyNumber')?.setValue(value.number);
    this.customerForm.get('companyDetails.contact')?.setValue('');
    this.loadCompanyPersonnel(value.number, value.type);
  }

  private loadCompanyPersonnel(companyNumber: string, companyType: string): void {
    this.fetchingPersonnel.set(true);
    this.regService.getCompanyPersonnel(companyNumber, companyType).pipe(
      catchError((error) => {
        console.error('Error fetching company personnel:', error);
        return of([]);
      })
    ).subscribe(personnel => {
      this.companyPersonnel.set(personnel);
      this.fetchingPersonnel.set(false);
    });
  }

  onContactSelected(contactId: string): void {
    if (contactId === 'add_new') {
      // Clear form fields for new contact
      this.customerForm.get('personalDetails.firstName')?.setValue('');
      this.customerForm.get('personalDetails.lastName')?.setValue('');
      this.customerForm.get('personalDetails.email')?.setValue('');
      this.customerForm.get('personalDetails.phone')?.setValue('');
      return;
    }

    const selectedContact = this.companyPersonnel().find(person => 
      `${person.firstName}_${person.lastName}_${person.email}` === contactId
    );

    if (selectedContact) {
      this.customerForm.get('personalDetails.firstName')?.setValue(selectedContact.firstName);
      this.customerForm.get('personalDetails.lastName')?.setValue(selectedContact.lastName);
      this.customerForm.get('personalDetails.email')?.setValue(selectedContact.email);
      this.customerForm.get('personalDetails.phone')?.setValue(selectedContact.phone);
    }
  }

  displayCompanyName(value: CompanyDetails | string): string {
    if (typeof value === 'string') {
      return value;
    }
    return value?.name || '';
  }

  openAddCompanyDialog(): void {
    const currentCompanyName = this.customerForm.get('companyDetails.companyName')?.value || '';
    
    const dialogRef = this.dialog.open(AddCompanyDialogComponent, {
      data: { companyName: currentCompanyName },
      width: '500px',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe((result: AddCompanyDialogResult) => {
      if (result) {
        // Update the form with the new company details
        this.customerForm.get('companyDetails.companyName')?.setValue(result.companyName);
        this.customerForm.get('companyDetails.companyNumber')?.setValue(result.companyId);
        
        // Set selected company state
        const newCompany: CompanyDetails = {
          name: result.companyName,
          number: result.companyId,
          type: 'LTD',
          companyStatus: 'ACTIVE'
        };
        this.selectedCompany.set(newCompany);
        
        // Clear personnel and set contact to "add new"
        this.companyPersonnel.set([]);
        this.customerForm.get('companyDetails.contact')?.setValue('add_new');
        
        // Clear personal details for new contact
        this.customerForm.get('personalDetails.firstName')?.setValue('');
        this.customerForm.get('personalDetails.lastName')?.setValue('');
        this.customerForm.get('personalDetails.email')?.setValue('');
        this.customerForm.get('personalDetails.phone')?.setValue('');
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: CustomerDetailsValue): void {
    if (value) {
      this.customerForm.patchValue(value, { emitEvent: false });
      this.registeringAs.set(value.registeringAs || 'individual');
    }
  }

  registerOnChange(fn: (value: CustomerDetailsValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.customerForm.disable();
    } else {
      this.customerForm.enable();
      // Re-apply company details disable logic if needed
      if (!this.isCompanySelected()) {
        this.customerForm.get('companyDetails')?.disable();
      }
    }
  }

  // Helper method to check if the form is valid
  get valid(): boolean {
    const personalDetailsValid = this.customerForm.get('personalDetails')?.valid;
    
    if (this.isCompanySelected()) {
      const companyDetailsValid = this.customerForm.get('companyDetails')?.valid;
      return !!(personalDetailsValid && companyDetailsValid);
    }
    
    return !!personalDetailsValid;
  }

  // Validator interface implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.customerForm) {
      return null;
    }

    const personalDetailsValid = this.customerForm.get('personalDetails')?.valid;
    
    if (this.isCompanySelected()) {
      const companyDetailsValid = this.customerForm.get('companyDetails')?.valid;
      const isValid = personalDetailsValid && companyDetailsValid;
      return isValid ? null : { customerDetailsInvalid: true };
    }
    
    return personalDetailsValid ? null : { customerDetailsInvalid: true };
  }
}
